import { validateManufactureYear } from "@/lib/manufactureYear";
import { z } from "zod";
import { phoneZodSchema } from "@/lib/phone-validation";
import type { Database } from "@/integrations/supabase/types";

export type Asset = Database["public"]["Tables"]["assets"]["Row"];
export type AssetInsert = Database["public"]["Tables"]["assets"]["Insert"];
export type AssetDocument = Database["public"]["Tables"]["asset_documents"]["Row"];
export type AssetAllocation = Database["public"]["Tables"]["asset_allocations"]["Row"];

export const ASSET_STATUSES = ["In Use", "In Store", "Under Repair", "Idle", "Condemned", "Disposed"] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const CRITICALITIES = ["low", "medium", "high", "critical", "life_support"] as const;
export type Criticality = (typeof CRITICALITIES)[number];

export const FUNDING_SOURCES = ["Own Funds", "Loan", "Grant", "CSR", "Donation", "Lease"] as const;
export const GST_RATES = [0, 5, 12, 18, 28] as const;

export const ALLOCATION_STATUSES = ["Unallocated", "Allocated", "Partially Allocated", "Returned"] as const;
export type AllocationStatus = (typeof ALLOCATION_STATUSES)[number];

export const ALLOCATION_TYPES = ["Initial Allocation", "Reallocation", "Partial Allocation"] as const;
export type AllocationType = (typeof ALLOCATION_TYPES)[number];

export const DOC_TYPES = [
  "Purchase Invoice", "Warranty Card", "Installation Report",
  "User Manual", "OEM PM Checklist", "Photo", 
  "Purchase Order", "Delivery Note", "Payment Document", 
  "Tender / GeM / Quotation Document", "Other Supporting Documents", 
  "Other",
] as const;

// ---------------- Asset Classification ----------------
export const ASSET_CLASSES = ["Equipment", "Infrastructure", "Vehicle", "IT", "Furniture", "Other"] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export const ASSET_TYPES = [
  "Building", "Medical Equipment", "Utility", "Vehicle",
  "Ambulance", "Service Vehicle", "Staff Vehicle", "Utility Vehicle", "Two Wheeler",
  "IT Asset", "Furniture", "Other",
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_NATURES = ["Major", "Minor"] as const;
export type AssetNature = (typeof ASSET_NATURES)[number];


export const BUILDING_OWNERSHIP = ["Owned", "Leased", "Rented"] as const;
export const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"] as const;

// Preprocessors for optional numeric fields — must be declared before any schema that uses them.
const optionalYear = z.preprocess(
  (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
  z.coerce.number().int().min(1800).max(2100).optional(),
);
// QA ISSUE-9377: manufacture year is a whole year from 1900 up to the current year (evaluated when validating, not at load)
const optionalManufactureYear = z.preprocess(
  (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
  z.coerce.number({ invalid_type_error: "Year of manufacture must be a whole number" }).superRefine((n, ctx) => {
    const problem = validateManufactureYear(n);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  }).optional(),
);
const optionalInt = z.preprocess(
  (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
  z.coerce.number().int().min(0).optional(),
);
const optionalIntMin1 = z.preprocess(
  (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
  z.coerce.number().int().min(1).optional(),
);

// ---------------- Asset create/edit form ----------------
// Branch / location / custodian are no longer captured here — they are set
// later via the Allocate flow.
export const step1Schema = z.object({
  name: z.string().trim().min(1, "Asset name is required").max(200),
  // Optional at the schema level; superRefine enforces per-class rules.
  category_id: z.string().uuid("Select a category").optional().or(z.literal("")),
  // UI-only: the top-level category picked before the asset name (mirrors the picker so it can be validated; never sent to the API)
  parent_category_id: z.string().optional().or(z.literal("")),
  make: z.string().trim().max(100).optional().or(z.literal("")),
  model: z.string().trim().max(100).optional().or(z.literal("")),
  serial_number: z.string().trim().max(100).optional().or(z.literal("")),
  year_of_manufacture: optionalManufactureYear,
  status: z.enum(ASSET_STATUSES).optional(), // Optional because it can be overridden by initial_allocation_type
  criticality: z.enum(CRITICALITIES),
  parent_asset_id: z.string().uuid().optional().or(z.literal("")),
  asset_quantity: z.coerce.number().int().min(1, "Quantity must be at least 1").max(100000),
  is_group_asset: z.boolean().default(false),
  // Classification
  asset_class: z.enum(ASSET_CLASSES).default("Equipment"),
  // Optional at the base level so a missing nature does not stop the cross-field rules below from running (QA ISSUE-9376):
  // every missing required field of the step is then reported together. superRefine adds "Asset Nature is required".
  asset_nature: z.preprocess((v) => (v === "" || v === null ? undefined : v), z.enum(ASSET_NATURES).optional()),
  asset_type: z.string().max(80).optional().or(z.literal("")),
  item_description: z.string().max(2000).optional().or(z.literal("")),
  remarks: z.string().max(2000).optional().or(z.literal("")),
  // Initial allocation fields
  initial_allocation_type: z.enum(["in_store", "allocated"]).optional().or(z.literal("")),
  initial_assignment_type: z.enum(["department", "internal_user", "third_party"]).optional().or(z.literal("")),
  initial_department_id: z.string().uuid().optional().or(z.literal("")),
  initial_user_id: z.string().uuid().optional().or(z.literal("")),
  initial_location_id: z.string().uuid().optional().or(z.literal("")),
  initial_store_id: z.string().uuid().optional().or(z.literal("")),
  // Initial third party fields
  initial_tp_name: z.string().max(200).optional().or(z.literal("")),
  initial_tp_type: z.string().max(100).optional().or(z.literal("")),
  initial_tp_contact: z.string().max(200).optional().or(z.literal("")),
  initial_tp_phone: phoneZodSchema,
  initial_tp_email: z.string().email().optional().or(z.literal("")),
  initial_tp_address: z.string().max(500).optional().or(z.literal("")),
  initial_allocation_date: z.string().optional().or(z.literal("")),
  requires_onboarding: z.boolean().default(false),
});

export const step2Schema = z.object({
  supplier_id: z.string().uuid().optional().or(z.literal("")),
  po_number: z.string().max(100).optional().or(z.literal("")),
  invoice_number: z.string().max(100).optional().or(z.literal("")),
  invoice_date: z.string().optional().or(z.literal("")),
  basic_cost: z.coerce.number().min(0).optional(),
  gst_rate: z.coerce.number().min(0).max(100).optional(),
  gst_amount: z.coerce.number().min(0).optional(),
  freight_cost: z.coerce.number().min(0).default(0),
  installation_cost: z.coerce.number().min(0).default(0),
  total_capitalised_value: z.coerce.number().min(0).optional(),
  funding_source: z.enum(FUNDING_SOURCES).optional(),
  capitalisation_date: z.string().optional().or(z.literal("")),
});

export const CALIBRATION_INTERVAL_UNITS = ["days", "months", "years"] as const;
export type CalibrationIntervalUnit = (typeof CALIBRATION_INTERVAL_UNITS)[number];

// Empty strings / NaN coming from unfilled numeric inputs must be treated as
// "not provided" so the wizard's Next button never blocks when the user
// leaves calibration fields blank (they are only required when
// calibration_required is ticked — enforced in fullAssetSchema.superRefine).
// (preprocessors are declared above, before step1Schema)

export const step3Schema = z.object({
  installation_date: z.string().optional().or(z.literal("")),
  warranty_start_date: z.string().optional().or(z.literal("")),
  warranty_end_date: z.string().optional().or(z.literal("")),
  expected_useful_life: optionalInt,
  requires_aerb: z.boolean().optional().default(false),
  aerb_licence_no: z.string().optional().or(z.literal("")),
  aerb_licence_valid_till: z.string().optional().or(z.literal("")),
  requires_pndt: z.boolean().optional().default(false),
  pndt_reg_no: z.string().optional().or(z.literal("")),
  pndt_valid_till: z.string().optional().or(z.literal("")),
  // Calibration configuration (asset-level; may inherit defaults from Asset Master)
  calibration_required: z.boolean().optional().default(false),
  calibration_interval_value: optionalIntMin1,
  calibration_interval_unit: z.enum(CALIBRATION_INTERVAL_UNITS).optional().or(z.literal("")),
  last_calibration_date: z.string().optional().or(z.literal("")),
  calibration_agency: z.string().max(200).optional().or(z.literal("")),
  calibration_certificate_required: z.boolean().optional().default(false),
  calibration_reminder_days: z.preprocess(
    (v) => (v === "" || v === null || (typeof v === "number" && Number.isNaN(v)) ? undefined : v),
    z.coerce.number().int().min(0).max(365).optional(),
  ),
});

// Building-specific optional fields.
export const buildingSchema = z.object({
  building_name: z.string().max(200).optional().or(z.literal("")),
  building_code: z.string().max(60).optional().or(z.literal("")),
  campus_address: z.string().max(500).optional().or(z.literal("")),
  land_area: z.coerce.number().min(0).optional().or(z.nan()),
  builtup_area: z.coerce.number().min(0).optional().or(z.nan()),
  number_of_floors: z.coerce.number().int().min(0).optional().or(z.nan()),
  construction_year: optionalYear,
  ownership_type: z.string().max(40).optional().or(z.literal("")),
  property_value: z.coerce.number().min(0).optional().or(z.nan()),
  insurance_required: z.boolean().optional().default(false),
  fire_noc_required: z.boolean().optional().default(false),
  compliance_required: z.boolean().optional().default(false),
  occupancy_type: z.string().max(80).optional().or(z.literal("")),
  building_usage: z.string().max(80).optional().or(z.literal("")),
  structural_status: z.string().max(80).optional().or(z.literal("")),
  last_inspection_date: z.string().optional().or(z.literal("")),
  building_remarks: z.string().max(1000).optional().or(z.literal("")),
});

// Vehicle-specific optional fields.
export const vehicleSchema = z.object({
  vehicle_registration_number: z.string().max(40).optional().or(z.literal("")),
  chassis_number: z.string().max(60).optional().or(z.literal("")),
  engine_number: z.string().max(60).optional().or(z.literal("")),
  vehicle_type: z.string().max(60).optional().or(z.literal("")),
  vehicle_make: z.string().max(80).optional().or(z.literal("")),
  vehicle_model: z.string().max(80).optional().or(z.literal("")),
  vehicle_manufacture_year: optionalYear,
  fuel_type: z.string().max(30).optional().or(z.literal("")),
  seating_capacity: z.coerce.number().int().min(0).optional().or(z.nan()),
  odometer_reading: z.coerce.number().min(0).optional().or(z.nan()),
  vehicle_ownership_type: z.string().max(30).optional().or(z.literal("")),
  assigned_driver: z.string().max(120).optional().or(z.literal("")),
  rc_expiry_date: z.string().optional().or(z.literal("")),
  vehicle_insurance_expiry_date: z.string().optional().or(z.literal("")),
  fitness_expiry_date: z.string().optional().or(z.literal("")),
  puc_expiry_date: z.string().optional().or(z.literal("")),
  road_tax_expiry_date: z.string().optional().or(z.literal("")),
  permit_expiry_date: z.string().optional().or(z.literal("")),
  gps_device_number: z.string().max(60).optional().or(z.literal("")),
  fastag_number: z.string().max(60).optional().or(z.literal("")),
  last_service_date: z.string().optional().or(z.literal("")),
  next_service_due_date: z.string().optional().or(z.literal("")),
  service_interval_km: z.coerce.number().min(0).optional().or(z.nan()),
  vehicle_remarks: z.string().max(1000).optional().or(z.literal("")),
  // New Vehicle Purchase Fields
  supplier_manufacturer: z.string().optional().or(z.literal("")),
  supplier_dealer_branch: z.string().optional().or(z.literal("")),
  supplier_gstin: z.string().optional().or(z.literal("")),
  supplier_contact_person: z.string().optional().or(z.literal("")),
  supplier_contact_number: phoneZodSchema,
  supplier_email: z.string().optional().or(z.literal("")),
  supplier_address: z.string().optional().or(z.literal("")),
  purchase_type: z.string().optional().or(z.literal("")),
  purchase_reference_number: z.string().optional().or(z.literal("")),
  po_date: z.string().optional().or(z.literal("")),
  delivery_date: z.string().optional().or(z.literal("")),
  registration_date: z.string().optional().or(z.literal("")),
  discount_amount: z.coerce.number().min(0).optional().or(z.nan()),
  body_fabrication_cost: z.coerce.number().min(0).optional().or(z.nan()),
  medical_equipment_cost: z.coerce.number().min(0).optional().or(z.nan()),
  accessories_cost: z.coerce.number().min(0).optional().or(z.nan()),
  registration_charges: z.coerce.number().min(0).optional().or(z.nan()),
  initial_insurance_premium: z.coerce.number().min(0).optional().or(z.nan()),
  road_tax_amount: z.coerce.number().min(0).optional().or(z.nan()),
  other_incidental_cost: z.coerce.number().min(0).optional().or(z.nan()),
  other_cost_remarks: z.string().optional().or(z.literal("")),
  warranty_period: z.coerce.number().min(0).optional().or(z.nan()),
  warranty_coverage: z.string().optional().or(z.literal("")),
  authorised_service_centre: z.string().optional().or(z.literal("")),
  free_service_count: z.coerce.number().min(0).optional().or(z.nan()),
});

export const fullAssetSchema = step1Schema
  .merge(step2Schema)
  .merge(step3Schema)
  .merge(buildingSchema)
  .merge(vehicleSchema)
  .superRefine((v, ctx) => {
    if (!v.asset_nature) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["asset_nature"], message: "Asset Nature is required" });
    }
    const cls = v.asset_class ?? "Equipment";
    const isBuilding = cls === "Infrastructure" || v.asset_type === "Building";
    const isVehicle = cls === "Vehicle";
    // Category is no longer required for Equipment — Asset Name (picked from
    // Asset Master or free-typed) is the primary label; category_id is set
    // automatically when the user picks a master item.
    if (!isBuilding && !isVehicle) {
      if (!v.parent_category_id && !v.category_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["parent_category_id"], message: "Asset Category is required" });
      }
      if (!v.make || !v.make.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["make"], message: "Manufacturer is required" });
      }
      if (!v.model || !v.model.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["model"], message: "Model Number is required" });
      }
      if (!v.serial_number || !v.serial_number.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["serial_number"], message: "Serial Number is required" });
      }
    }
    if (isBuilding) {
      if (!v.building_name || !v.building_name.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["building_name"], message: "Building name is required" });
      }
      if (!v.ownership_type) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["ownership_type"], message: "Ownership type is required" });
      }
    }
    if (isVehicle) {
      if (!v.vehicle_registration_number || !v.vehicle_registration_number.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["vehicle_registration_number"], message: "Registration number is required" });
      }
      if (!v.vehicle_type) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["vehicle_type"], message: "Vehicle type is required" });
      }
      if (!v.vehicle_ownership_type) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["vehicle_ownership_type"], message: "Ownership type is required" });
      }
      if (!v.supplier_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["supplier_id"], message: "Supplier / Authorised Dealer is required for vehicles" });
      }
      if (!v.purchase_type) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["purchase_type"], message: "Purchase Type is required for vehicles" });
      }
      const pType = v.purchase_type || "";
      if (["Direct Purchase", "Tender", "GeM"].includes(pType)) {
        if (!v.po_number) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["po_number"], message: "PO Number is required for this purchase type" });
        if (!v.po_date) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["po_date"], message: "PO Date is required for this purchase type" });
      }
      if (v.po_date && v.invoice_date && new Date(v.invoice_date) < new Date(v.po_date)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["invoice_date"], message: "Invoice Date cannot be earlier than PO Date" });
      }
      if (v.invoice_date && v.delivery_date && new Date(v.delivery_date) < new Date(v.invoice_date)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["delivery_date"], message: "Delivery Date cannot be earlier than Invoice Date" });
      }
      if (v.delivery_date && v.capitalisation_date && new Date(v.capitalisation_date) < new Date(v.delivery_date)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["capitalisation_date"], message: "Capitalisation Date cannot be earlier than Delivery Date" });
      }
      if (v.warranty_start_date && v.warranty_end_date && new Date(v.warranty_end_date) <= new Date(v.warranty_start_date)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["warranty_end_date"], message: "Warranty End Date must be later than Warranty Start Date" });
      }
      if (v.discount_amount && v.basic_cost && v.discount_amount > v.basic_cost) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["discount_amount"], message: "Discount Amount cannot exceed Basic Vehicle Cost" });
      }
    }
    // Calibration is only required when the "Calibration Required" toggle is on.
    if (v.calibration_required) {
      if (!v.calibration_interval_value || Number(v.calibration_interval_value) < 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["calibration_interval_value"], message: "Interval value is required" });
      }
      if (!v.calibration_interval_unit) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["calibration_interval_unit"], message: "Interval unit is required" });
      }
    }
    
    // Initial allocation requirements
    if (v.initial_allocation_type === "in_store" && !v.initial_store_id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["initial_store_id"], message: "Store is required when keeping in store" });
    }
    if (v.initial_allocation_type === "allocated") {
      if (!v.initial_allocation_date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["initial_allocation_date"], message: "Allocation date is required" });
      }
      if (v.initial_assignment_type === "department" && !v.initial_department_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["initial_department_id"], message: "Department is required" });
      }
      if (v.initial_assignment_type === "internal_user" && !v.initial_user_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["initial_user_id"], message: "User is required" });
      }
      if (v.initial_assignment_type === "third_party" && !v.initial_tp_name?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["initial_tp_name"], message: "Third party name is required" });
      }
    }
  });

export type AssetFormValues = z.infer<typeof fullAssetSchema>;

export const defaultAssetForm = (): Partial<AssetFormValues> => ({
  name: "",
  make: "",
  model: "",
  serial_number: "",
  category_id: "",
  parent_category_id: "",
  status: "In Use",
  criticality: "medium",
  asset_quantity: 1,
  is_group_asset: false,
  freight_cost: 0,
  installation_cost: 0,
  gst_rate: 18,
  asset_class: "Equipment",
  asset_type: "",
  insurance_required: false,
  fire_noc_required: false,
  compliance_required: false,
  initial_allocation_type: "in_store",
  requires_onboarding: false,
});

// ---------------- Allocation form ----------------
export const allocationSchema = z.object({
  asset_id: z.string().uuid(),
  branch_id: z.string().uuid("Select a branch"),
  location_id: z.string().uuid("Select a location"),
  custodian_dept_id: z.string().uuid("Select a custodian department"),
  custodian_user_id: z.string().uuid().optional().or(z.literal("")),
  quantity: z.coerce.number().int().min(1, "Quantity must be at least 1"),
  remarks: z.string().max(500).optional().or(z.literal("")),
  asset_nature: z.string().optional().or(z.literal("")),
});
export type AllocationFormValues = z.infer<typeof allocationSchema>;
